import type {
  ActionType,
  WorkflowActionCatalogEntry,
  WorkflowActionContactLink,
} from '@super/shared-types'

export interface DescribableAction {
  actionType: ActionType
  actionSubtype?: string | null
  parameters: unknown
  contacts?: WorkflowActionContactLink[]
}

export interface DirectoryLookup {
  roles: Array<{
    id: string
    name: string
    /** Role members, when the caller has them. A transfer recipient names
     *  the member it came from, so this is what ties a number to a person. */
    members?: Array<{ id: string; staffId: string }>
  }>
  staff: Array<{ id: string; name: string }>
}

/** The lookup `describeAction` reads, built from the directory context. */
export function directoryLookupFrom(
  roles: Array<{
    id: string
    name: string
    members?: Array<{ id: string; staffId: string }>
  }>,
  staff: Array<{ id: string; name: string }>,
): DirectoryLookup {
  return {
    roles: roles.map(r => ({
      id: r.id,
      name: r.name,
      members: (r.members ?? []).map(m => ({ id: m.id, staffId: m.staffId })),
    })),
    staff: staff.map(s => ({ id: s.id, name: s.name })),
  }
}

function resolveContactLabel(
  contacts: WorkflowActionContactLink[] | undefined,
  directory: DirectoryLookup | undefined,
): string | null {
  if (!contacts?.length || !directory) return null
  const labels: string[] = []
  for (const c of contacts) {
    if (c.staffId) {
      const match = directory.staff.find(s => s.id === c.staffId)
      if (match) labels.push(`@${match.name}`)
    } else if (c.roleId) {
      const match = directory.roles.find(r => r.id === c.roleId)
      if (match) labels.push(`@${match.name}`)
    }
  }
  return labels.length ? labels.join(', ') : null
}

export function formatPhone(raw: string | null | undefined): string {
  if (!raw) return ''
  const digits = raw.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return raw
}

interface Recipient {
  number?: string | null
  extension?: string | null
  sourceRoleMemberId?: string | null
  staffId?: string | null
}

function formatRecipientNumber(r: Recipient): string {
  const base = formatPhone(r.number ?? null)
  return r.extension ? `${base} ext ${r.extension}` : base
}

type RoleRun = { roleId: string; index: number; names: string[] }

/** Recipients as people where the directory knows them: a run of members
 *  from one role collapses to "@Role (Ann, Ben)", a staff pick reads
 *  "@Ann", anything else stays a number. */
export function describeTransferRecipients(
  recipients: Recipient[],
  directory?: DirectoryLookup,
): string[] {
  const labels: string[] = []
  // The open run lives on an object so the flush closure and the loop read
  // one place; a plain `let` is narrowed to null by the checker here.
  const run: { current: RoleRun | null } = { current: null }
  const flushRole = () => {
    const open = run.current
    if (!open) return
    const role = directory?.roles.find(r => r.id === open.roleId)
    const names = open.names.length ? ` (${open.names.join(', ')})` : ''
    labels[open.index] = `@${role?.name ?? 'role'}${names}`
    run.current = null
  }
  for (const r of recipients) {
    const memberId = r.sourceRoleMemberId
    const role = memberId
      ? directory?.roles.find(role =>
          role.members?.some(m => m.id === memberId),
        )
      : undefined
    if (role && memberId) {
      const member = role.members?.find(m => m.id === memberId)
      const staffName = member
        ? directory?.staff.find(s => s.id === member.staffId)?.name
        : undefined
      if (run.current?.roleId !== role.id) {
        flushRole()
        run.current = { roleId: role.id, index: labels.length, names: [] }
        labels.push('')
      }
      const open = run.current
      if (staffName && open && !open.names.includes(staffName)) {
        open.names.push(staffName)
      }
      continue
    }
    flushRole()
    const staff = r.staffId
      ? directory?.staff.find(s => s.id === r.staffId)
      : undefined
    labels.push(staff ? `@${staff.name}` : formatRecipientNumber(r))
  }
  flushRole()
  return labels.filter(Boolean)
}

/** The one-line label an action shows in the catalog table, picker, and group
 *  member list — "Transfer to (708) 203-5932", "Email notify a@x.com", … */
export function describeAction(
  entry: DescribableAction,
  directory?: DirectoryLookup,
): string {
  const params = (entry.parameters ?? null) as Record<string, unknown> | null
  const contactLabel = resolveContactLabel(entry.contacts, directory)
  switch (entry.actionType) {
    case 'TRANSFER': {
      if (contactLabel) return `Transfer to ${contactLabel}`
      const recipients = (params?.recipients as Recipient[] | undefined) ?? []
      const labels = describeTransferRecipients(recipients, directory)
      return labels.length
        ? `Transfer to ${labels.join(', ')}`
        : 'Transfer (no recipients)'
    }
    case 'EMAIL': {
      const email = (params?.email as string | undefined) ?? ''
      const target = contactLabel ?? email
      return target ? `Email notify ${target}` : 'Email notify (no recipient)'
    }
    case 'SMS': {
      const number = (params?.number as string | undefined) ?? ''
      const target = contactLabel ?? formatPhone(number)
      return target ? `SMS notify ${target}` : 'SMS notify (no recipient)'
    }
    case 'PHONE_CALL': {
      const number = (params?.number as string | undefined) ?? ''
      const target = contactLabel ?? formatPhone(number)
      return target ? `Call ${target}` : 'Call (no recipient)'
    }
    case 'PUSH_INTEGRATION': {
      const subtype = entry.actionSubtype?.replace(/_/g, ' ').toLowerCase()
      return subtype ? `Push ${subtype}` : 'Push integration'
    }
    default:
      return entry.actionType.replace(/_/g, ' ')
  }
}

export function buildCatalogDeleteMessage(
  label: string,
  count: number,
  emptyAfter: number,
): string {
  const header = `Delete "${label}" rule?`
  if (count === 0) return `${header}\n\nIt isn't used by any group.`
  const nonEmpty = count - emptyAfter
  const groupWord = count === 1 ? 'group' : 'groups'
  const lines = [`${header}\n`, `${count} ${groupWord} reference this action`]
  if (emptyAfter > 0) {
    lines.push(
      `⚠ ${emptyAfter} ${emptyAfter === 1 ? 'group' : 'groups'} will become empty`,
    )
  }
  if (nonEmpty > 0) {
    lines.push(
      `✓ ${nonEmpty} ${nonEmpty === 1 ? 'group' : 'groups'} will continue to function with other actions`,
    )
  }
  return lines.join('\n')
}

/** Returns the raw-text haystack a catalog entry is searchable by:
 *  describe label + resolved name + description + parameter values +
 *  linked contact names when a directory lookup is provided. */
export function searchHaystackForAction(
  entry: WorkflowActionCatalogEntry,
  directory?: DirectoryLookup,
): string {
  const parts: string[] = [
    describeAction(entry, directory),
    entry.resolvedName ?? '',
    entry.description ?? '',
  ]
  if (directory && entry.contacts) {
    for (const c of entry.contacts) {
      if (c.staffId) {
        const match = directory.staff.find(s => s.id === c.staffId)
        if (match) parts.push(match.name)
      } else if (c.roleId) {
        const match = directory.roles.find(r => r.id === c.roleId)
        if (match) parts.push(match.name)
      }
    }
  }
  const params = (entry.parameters ?? null) as Record<string, unknown> | null
  if (params) {
    if (typeof params.email === 'string') parts.push(params.email)
    if (typeof params.number === 'string') {
      parts.push(params.number, formatPhone(params.number))
    }
    const recipients = params.recipients as Recipient[] | undefined
    if (recipients) {
      for (const r of recipients) {
        if (r.number) parts.push(r.number, formatPhone(r.number))
        if (r.extension) parts.push(r.extension)
      }
    }
  }
  return parts.join(' ').toLowerCase()
}
